'use client'

import Filter from '@/components/forms/filter'
import ClockIcon from '@/components/icons/clock-icon'
import MagnifyingGlassIcon from '@/components/icons/magnifying-glass'
import Pager from '@/components/search/pager'
import { Accordion } from '@/components/ui/accordion'
import { Input } from '@/components/ui/input'
import { dayOptions } from '@/helpers/days'
import { cn } from '@/lib/utils'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState, useTransition } from 'react'
import FilterCheckbox from './checkbox'
import MobileFilterWrapper, {
  AccordionItemWrapper,
} from './mobile-filter-wrapper'

// Max championships to show per page
const MAX_CHAMPIONSHIPS = 12
const RACE_TYPES = ['Single Event', 'Championship Event']
const SORT_OPTIONS = ['Live', 'Upcoming', 'Finished']

export default function RacingShell({
  community,
  facets,
  initialSortOrder,
  total,
  children,
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()

  // Local state for text search to allow typing without firing request on every keystroke
  const [textSearch, setTextSearch] = useState(searchParams.get('q') || '')

  // Debounce text search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (textSearch !== (searchParams.get('q') || '')) {
        updateFilter('q', textSearch)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [textSearch])

  // Get current filters from URL
  const platformFilter = searchParams.getAll('platform')
  const gameFilter = searchParams.getAll('game')
  const raceTypeFilter = searchParams.getAll('raceType')
  const carFilter = searchParams.getAll('car')
  const dayFilter = searchParams.getAll('day')
  const sortOrder = searchParams.get('sort') || initialSortOrder || 'Upcoming'
  const pageNum = Number(searchParams.get('page')) || 1

  const platformFilterOptions = facets?.platforms || []
  const gameFilterOptions = facets?.games || []
  const carFilterOptions = facets?.cars || []

  // Helper to update URL params
  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams)

    // Reset page on filter change (unless it's a page change itself)
    if (key !== 'page') {
      params.delete('page')
    }

    if (Array.isArray(value)) {
      params.delete(key)
      value.forEach((v) => params.append(key, v))
    } else if (value === null || value === '') {
      params.delete(key)
    } else {
      params.set(key, value)
    }

    startTransition(() => {
      router.push(`${pathname}?${params.toString()}`, { scroll: false })
    })
  }

  const handleToggleFilter = (key, value, currentValues) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value]
    updateFilter(key, newValues)
  }

  function handleClear() {
    startTransition(() => {
      router.push(pathname, { scroll: false })
      setTextSearch('')
    })
  }

  const handlePageChange = (newPageNum) => {
    updateFilter('page', newPageNum)
  }

  const filterHasBeenSet =
    platformFilter.length > 0 ||
    gameFilter.length > 0 ||
    raceTypeFilter.length > 0 ||
    carFilter.length > 0 ||
    dayFilter.length > 0 ||
    (searchParams.get('q') || '').length > 0

  return (
    <div className={cn(isPending && 'opacity-70 transition-opacity')}>
      <div className="col-span-full mt-6 flex items-center rounded-sm desktop-xs:col-span-10 desktop-xs:col-start-2">
        {/* Mobile Filter */}
        <MobileFilterWrapper onClear={handleClear}>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItemWrapper value="platforms" title="Platforms">
              {platformFilterOptions.map((pO, i) => (
                <FilterCheckbox
                  checked={platformFilter.includes(pO.name)}
                  key={'platformFilterOption' + i}
                  label={pO.nicename}
                  onChange={() =>
                    handleToggleFilter('platform', pO.name, platformFilter)
                  }
                />
              ))}
            </AccordionItemWrapper>
            <AccordionItemWrapper value="games" title="Games">
              {gameFilterOptions.map((gO, i) => (
                <FilterCheckbox
                  checked={gameFilter.includes(gO.name)}
                  key={'gameFilterOption' + i}
                  label={gO.nicename}
                  onChange={() =>
                    handleToggleFilter('game', gO.name, gameFilter)
                  }
                />
              ))}
            </AccordionItemWrapper>
            <AccordionItemWrapper value="raceType" title="Race Types">
              {RACE_TYPES.map((rtO, i) => (
                <FilterCheckbox
                  checked={raceTypeFilter.includes(rtO)}
                  key={'raceTypeFilterOption' + i}
                  label={rtO}
                  onChange={() =>
                    handleToggleFilter('raceType', rtO, raceTypeFilter)
                  }
                />
              ))}
            </AccordionItemWrapper>
            {carFilterOptions.length > 0 && (
              <AccordionItemWrapper value="cars" title="Car Classes">
                {carFilterOptions.map((cO, i) => (
                  <FilterCheckbox
                    checked={carFilter.includes(cO.id)}
                    key={'carFilterOption' + i}
                    label={cO.name}
                    onChange={() => handleToggleFilter('car', cO.id, carFilter)}
                  />
                ))}
              </AccordionItemWrapper>
            )}
            <AccordionItemWrapper value="days" title="Days">
              {dayOptions.map((dO) => (
                <FilterCheckbox
                  key={dO.name}
                  label={dO.nicename}
                  checked={dayFilter.includes(dO.name)}
                  onChange={() => handleToggleFilter('day', dO.name, dayFilter)}
                />
              ))}
            </AccordionItemWrapper>
            <AccordionItemWrapper value="sortOrder" title="Sort Order">
              {SORT_OPTIONS.map((sO) => (
                <FilterCheckbox
                  key={sO}
                  label={sO}
                  checked={sortOrder === sO}
                  onChange={() => updateFilter('sort', sO)}
                />
              ))}
            </AccordionItemWrapper>
          </Accordion>
        </MobileFilterWrapper>
        {/* Desktop Filter */}
        <div className="col-span-full hidden w-full items-start justify-between gap-3 desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:flex">
          <div className="flex items-center gap-3">
            <p
              className={`text-p-1 text-neutral-blue-500 ${filterHasBeenSet ? 'cursor-pointer underline underline-offset-2' : ''}`}
              onClick={() => {
                if (filterHasBeenSet) {
                  handleClear()
                }
              }}
            >
              {filterHasBeenSet ? 'Reset' : 'Filter'}
            </p>
            <div className="flex flex-wrap gap-3">
              {platformFilterOptions.length > 1 && (
                <Filter
                  value={platformFilter}
                  setValue={(newVal) => updateFilter('platform', newVal)}
                  options={platformFilterOptions}
                  displayLabel="nicename"
                  valueLabel="name"
                  description="Platforms"
                  parentClassName="w-[157px] border-neutral-blue-700! py-4"
                  displayClassName="w-[170px]"
                  multi
                />
              )}
              {gameFilterOptions.length > 1 && (
                <Filter
                  value={gameFilter}
                  setValue={(newVal) => updateFilter('game', newVal)}
                  options={gameFilterOptions}
                  displayLabel="nicename"
                  valueLabel="name"
                  description="Games"
                  parentClassName="w-[157px] border-neutral-blue-700! py-4"
                  displayClassName="w-[170px]"
                  multi
                />
              )}
              <Filter
                value={raceTypeFilter}
                setValue={(newVal) => updateFilter('raceType', newVal)}
                options={RACE_TYPES}
                description="Race Types"
                parentClassName="w-[170px] border-neutral-blue-700! py-4"
                displayClassName="w-[200px]"
                multi
              />
              {carFilterOptions.length > 1 && (
                <Filter
                  value={carFilter}
                  setValue={(newVal) => updateFilter('car', newVal)}
                  options={carFilterOptions}
                  displayLabel="name"
                  valueLabel="id"
                  description="Cars"
                  parentClassName="w-[172px] border-neutral-blue-700! py-4"
                  displayClassName="w-[360px]"
                  multi
                />
              )}
              <Filter
                value={dayFilter}
                setValue={(newVal) => updateFilter('day', newVal)}
                options={dayOptions}
                displayLabel="nicename"
                valueLabel="name"
                description="Days"
                parentClassName="w-[125px] border-neutral-blue-700! py-4"
                displayClassName="w-[200px]"
                multi
              />
              <Filter
                value={sortOrder}
                setValue={(newVal) => updateFilter('sort', newVal)}
                options={SORT_OPTIONS}
                parentClassName="w-[143px] border-neutral-blue-700! py-4"
                displayClassName="w-[200px]"
              />
            </div>
          </div>
          <div className="relative flex h-[54px] min-w-[120px] max-w-[300px] flex-1 items-center">
            <Input
              className="border-neutral-blue-700! h-full pl-12"
              placeholder={`Search ${community.name}`}
              value={textSearch}
              onChange={(e) => setTextSearch(e.target.value)}
            />
            <MagnifyingGlassIcon className="pointer-events-none absolute ml-6 h-4 w-4 stroke-shades-0 stroke-2" />
          </div>
        </div>
      </div>

      <div className="divide col-span-full mt-6 hidden bg-neutral-blue-700 desktop-xs:block"></div>

      <div
        className={cn(
          'divide mt-6 h-px bg-warning-500 desktop-xs:mt-8',
          sortOrder === 'Live' && 'bg-success-500',
          sortOrder === 'Upcoming' && 'bg-warning-500',
          sortOrder === 'Finished' && 'bg-error-500',
        )}
      />

      <div className="mt-3 flex items-center justify-between desktop-xs:mt-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex size-8 items-center justify-center rounded-[5px] align-middle tablet:size-10 desktop-xs:size-12',
                sortOrder === 'Live' && 'bg-success-500',
                sortOrder === 'Upcoming' && 'bg-warning-500',
                sortOrder === 'Finished' && 'bg-error-500',
              )}
            >
              <ClockIcon className="size-[18px] stroke-white stroke-2 tablet:size-[20px] desktop-xs:size-[26px]" />
            </div>
            <h5 className="text-h-6 font-semibold desktop-xs:text-h-5">
              {sortOrder}
            </h5>
          </div>
          <p className="h-[23px] text-p-2 font-normal text-neutral-blue-200 desktop-xs:h-[25px] desktop-xs:text-p-3">
            {sortOrder === 'Live'
              ? `Browse live championships in the ${community.name} community.`
              : sortOrder === 'Upcoming'
                ? `Browse championships with upcoming events in the ${community.name} community.`
                : `View finished championships in the ${community.name} community.`}
          </p>
        </div>
      </div>

      {children}

      {total > MAX_CHAMPIONSHIPS && (
        <Pager
          className="col-span-full mt-10 desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:max-w-[408px] desktop-xs:justify-self-end"
          page={pageNum}
          pageLimit={Math.ceil(total / MAX_CHAMPIONSHIPS)}
          setPage={handlePageChange}
        />
      )}
    </div>
  )
}
